import React, { useState } from "react";
import classNames from "classnames";
import styles from "./index.module.css";
import TextInput from "shared/@forms/TextInput";
import PropTypes from "prop-types";

import { Plus, IkonateContext } from "react-ikonate";
import { Section } from "shared/layout";
import { useOptionData } from "../useOptionData";
import Button from "shared/Button";
import { ButtonPurpose } from "shared/Button/Button";
import _ from "lodash";
import pageStyles from "../../ContentEdit.module.css";

function TagOption({
  tag,
  selected,
  editing,
  markedForDeletion,
  onSelect,
  onToggleDelete,
}) {
  return (
    <div className={styles.tagWithDeleteWrapper}>
      <button
        type="button"
        className={classNames(styles.tagBtn, {
          [styles.selectedTag]: selected,
          [styles.tagMarkedForDeletion]: markedForDeletion,
        })}
        onClick={editing ? onToggleDelete : onSelect}
      >
        {tag.label}
      </button>
      {editing && (
        <button
          type="button"
          className={styles.deleteXButton}
          aria-label={`Mark ${tag.label} for deletion`}
          aria-pressed={markedForDeletion}
          onClick={onToggleDelete}
        >
          ×
        </button>
      )}
    </div>
  );
}

function TagSettingsSection({
  id,
  title,
  tagList,
  tags,
  setTags,
  data,
  editing,
  deletedTags,
  hiddenDeletedTags,
  saving,
  saveError,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onToggleDelete,
  hidden,
  internal,
}) {
  const [adding, setAdding] = useState(false);
  const [newTag, setNewTag] = useState(null);
  const visibleTags = tagList.filter((tag) => !hiddenDeletedTags.has(tag.id));
  const selectedTags = visibleTags.filter((tag) => tags.includes(tag.id));
  const availableTags = visibleTags.filter((tag) => !tags.includes(tag.id));

  const addTag = () => {
    data.addMutation
      .mutateAsync({ name: newTag, hidden, internal })
      .then(() => {
        setAdding(false);
        setNewTag(null);
      });
  };

  const startEdit = () => {
    setAdding(false);
    setNewTag(null);
    onStartEdit(id);
  };

  const renderTag = (tag, selected) => (
    <TagOption
      key={tag.id}
      tag={tag}
      selected={selected}
      editing={editing}
      markedForDeletion={deletedTags.has(tag.id)}
      onSelect={() => {
        setTags(
          selected
            ? tags.filter((tagId) => tagId !== tag.id)
            : [...tags, tag.id]
        );
      }}
      onToggleDelete={() => onToggleDelete(id, tag.id)}
    />
  );

  return (
    <Section>
      <header className={styles.sectionHeader}>
        <h4>{title}</h4>
        {editing ? (
          <div className={styles.headerButtonGroup}>
            <Button
              disabled={saving}
              onClick={() => onCancelEdit(id)}
              className={pageStyles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              purpose={ButtonPurpose.save}
              disabled={saving}
              onClick={() => void onSaveEdit(id)}
              className={pageStyles.saveButton}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        ) : (
          <Button
            size="small"
            onClick={startEdit}
            className={styles.editButton}
          >
            Edit
          </Button>
        )}
      </header>
      {saveError && (
        <div role="alert" className={styles.deleteError}>
          {saveError}
        </div>
      )}

      <div className={classNames(styles.row, styles.center)}>
        <div className={styles.selectedTags}>
          <div className={classNames(styles.row, styles.tagBtns)}>
            {selectedTags.length > 0 ? (
              selectedTags.map((tag) => renderTag(tag, true))
            ) : (
              <span>Click the tags below to add</span>
            )}
          </div>
        </div>
      </div>
      <div className={classNames(styles.row, styles.tagBtns)}>
        {availableTags.map((tag) => renderTag(tag, false))}
        {!editing &&
          (adding ? (
            <div className={styles.addBar}>
              <TextInput
                placeholder="Add a Tag"
                value={newTag}
                onChange={(event) => setNewTag(event.target.value)}
              />
              <Button
                disabled={data.addMutation.isLoading}
                onClick={() => {
                  setAdding(false);
                  setNewTag(null);
                }}
              >
                Cancel
              </Button>
              <Button
                purpose={ButtonPurpose.save}
                disabled={data.addMutation.isLoading}
                onClick={addTag}
              >
                {data.addMutation.isLoading ? "Adding..." : "Add"}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className={styles.add}
            >
              <IkonateContext.Provider value={{ size: 20, border: 1 }}>
                <Plus />
              </IkonateContext.Provider>
              Add
            </button>
          ))}
      </div>
    </Section>
  );
}

export default function Tags({
  keywordTags,
  hiddenTags,
  internalTags,
  tags,
  setTags,
}) {
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [deletedTagsBySectionId, setDeletedTagsBySectionId] = useState({});
  const [hiddenDeletedTagsBySectionId, setHiddenDeletedTagsBySectionId] =
    useState({});
  const [savingTagSectionId, setSavingTagSectionId] = useState(null);
  const [tagDeleteErrors, setTagDeleteErrors] = useState({});

  const data = useOptionData("tags");

  keywordTags = _.sortBy(keywordTags, (a) => a.label.toLowerCase());
  hiddenTags = _.sortBy(hiddenTags, (a) => a.label.toLowerCase());
  internalTags = _.sortBy(internalTags, (a) => a.label.toLowerCase());

  const toggleTagForDeletion = (sectionId, tagId) => {
    setDeletedTagsBySectionId((current) => {
      const nextTags = new Set(current[sectionId] || []);
      nextTags.has(tagId) ? nextTags.delete(tagId) : nextTags.add(tagId);
      return { ...current, [sectionId]: nextTags };
    });
    setTagDeleteErrors((current) => ({ ...current, [sectionId]: null }));
  };

  const cancelTagEdit = (sectionId) => {
    setEditingSectionId(null);
    setDeletedTagsBySectionId((current) => {
      const next = { ...current };
      delete next[sectionId];
      return next;
    });
    setTagDeleteErrors((current) => ({ ...current, [sectionId]: null }));
  };

  const saveTagEdit = async (sectionId) => {
    const deletedTags = deletedTagsBySectionId[sectionId] || new Set();
    if (deletedTags.size === 0) {
      setEditingSectionId(null);
      return;
    }

    setSavingTagSectionId(sectionId);
    const deletedIds = [];
    try {
      for (const tagId of deletedTags) {
        await data.deleteMutation.mutateAsync(tagId);
        deletedIds.push(tagId);
      }

      setHiddenDeletedTagsBySectionId((current) => ({
        ...current,
        [sectionId]: new Set([...(current[sectionId] || []), ...deletedIds]),
      }));
      setTags(tags.filter((tagId) => !deletedIds.includes(tagId)));
      setEditingSectionId(null);
      setDeletedTagsBySectionId((current) => {
        const next = { ...current };
        delete next[sectionId];
        return next;
      });
      setTagDeleteErrors((current) => ({ ...current, [sectionId]: null }));
    } catch (error) {
      if (deletedIds.length > 0) {
        setHiddenDeletedTagsBySectionId((current) => ({
          ...current,
          [sectionId]: new Set([...(current[sectionId] || []), ...deletedIds]),
        }));
        setTags(tags.filter((tagId) => !deletedIds.includes(tagId)));
      }
      setTagDeleteErrors((current) => ({
        ...current,
        [sectionId]: error?.message || "Failed to delete tags.",
      }));
    } finally {
      setSavingTagSectionId(null);
    }
  };

  return (
    <div className={classNames(styles.container)}>
      <TagSettingsSection
        id="keyword"
        title="Keyword Tags"
        tagList={keywordTags}
        tags={tags}
        setTags={setTags}
        data={data}
        editing={editingSectionId === "keyword"}
        deletedTags={deletedTagsBySectionId.keyword || new Set()}
        hiddenDeletedTags={hiddenDeletedTagsBySectionId.keyword || new Set()}
        saving={savingTagSectionId === "keyword"}
        saveError={tagDeleteErrors.keyword}
        onStartEdit={setEditingSectionId}
        onCancelEdit={cancelTagEdit}
        onSaveEdit={saveTagEdit}
        onToggleDelete={toggleTagForDeletion}
        hidden={false}
        internal={false}
      />
      <TagSettingsSection
        id="hidden"
        title="Additional Searchable | Hidden Tags"
        tagList={hiddenTags}
        tags={tags}
        setTags={setTags}
        data={data}
        editing={editingSectionId === "hidden"}
        deletedTags={deletedTagsBySectionId.hidden || new Set()}
        hiddenDeletedTags={hiddenDeletedTagsBySectionId.hidden || new Set()}
        saving={savingTagSectionId === "hidden"}
        saveError={tagDeleteErrors.hidden}
        onStartEdit={setEditingSectionId}
        onCancelEdit={cancelTagEdit}
        onSaveEdit={saveTagEdit}
        onToggleDelete={toggleTagForDeletion}
        hidden
        internal={false}
      />
      <TagSettingsSection
        id="internal"
        title="Internal Tags"
        tagList={internalTags}
        tags={tags}
        setTags={setTags}
        data={data}
        editing={editingSectionId === "internal"}
        deletedTags={deletedTagsBySectionId.internal || new Set()}
        hiddenDeletedTags={hiddenDeletedTagsBySectionId.internal || new Set()}
        saving={savingTagSectionId === "internal"}
        saveError={tagDeleteErrors.internal}
        onStartEdit={setEditingSectionId}
        onCancelEdit={cancelTagEdit}
        onSaveEdit={saveTagEdit}
        onToggleDelete={toggleTagForDeletion}
        hidden={false}
        internal
      />
    </div>
  );
}
Tags.propTypes = {
  tags: PropTypes.array,
};
